import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { PaymentPanel } from "@/components/PaymentPanel";
import { MyMemberships } from "@/components/MyMemberships";
import { MyAttendance } from "@/components/MyAttendance";
import { MyCommunity } from "@/components/MyCommunity";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { rupees, shortDate, dateTime, shortOrderRef, tradeLabel } from "@/lib/format";
import { isUuid } from "@/lib/ids";
import { usePageTitle } from "@/lib/usePageTitle";
import ConfirmDialog from "@/components/ConfirmDialog";

const OPEN_PAYMENT_STATUSES = ["pending", "awaiting_confirmation"];
const TASK_OPEN_STATUSES = ["open", "assigned"];

function byId(list) {
  // `rows`, not `list || []`: an object-shaped `{items, next_cursor}` response is truthy, so the
  // old guard sailed past it and `.map` threw — which is how this page went blank while every one
  // of its ten requests returned 200.
  return Object.fromEntries(rows(list).map((item) => [item.id, item]));
}

export default function MyActivity() {
  const { t, tenant, lang } = useApp();
  usePageTitle(t("activity.title")); // R3-10: per-page tab title
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [ordersById, setOrdersById] = useState({});
  const [appointmentsById, setAppointmentsById] = useState({});
  const [bookingsById, setBookingsById] = useState({});
  const [tasksById, setTasksById] = useState({});
  const [ambulanceById, setAmbulanceById] = useState({});
  const [reservations, setReservations] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentBusyId, setPaymentBusyId] = useState("");
  const [paymentErrors, setPaymentErrors] = useState({});
  const [disputes, setDisputes] = useState([]);
  const [reportingId, setReportingId] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  // Tracks the *initial* page load only. Later reload() calls (after a cancel, a dispute, a
  // payment claim) already surface their own failures via the calling action's toast, so they
  // are left alone — only the first load needs an honest "we could not load this" state, because
  // an unhandled rejection there used to leave `items` at its empty initial value forever, which
  // rendered identically to "you have no activity".
  const [loadError, setLoadError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // One failed endpoint used to hide eight healthy ones. `/activity/mine` is the only
  // LOAD-BEARING call — it is the list itself, and without it the page has nothing to show, so
  // its rejection still becomes the full-page error above. Everything else only ENRICHES a row
  // that the spine already returned, and the renderer already falls back to `renderFallback(item)`
  // whenever a detail map has no entry for an id. So those are settled independently: a dead
  // /disputes/mine now costs the dispute badges, not the orders.
  //
  // The degradation is announced rather than silent (§7.3) — `partialLoad` says some rows show
  // less than usual, and the reservations section gets its own error instead of rendering
  // "no reservations", which would be a failed request wearing an empty state's clothes.
  const [partialLoad, setPartialLoad] = useState(false);
  const [reservationsError, setReservationsError] = useState(false);

  const reload = async () => {
    const spine = await api.get("/activity/mine", {
      params: { tenant_id: tenant?.id, group: "all", limit: 100 },
    });
    setItems(rows(spine.data));

    const [o, a, b, r, tasks, ambulance, p, d] = await Promise.allSettled([
      api.get("/orders/mine"),
      api.get("/appointments/mine"),
      api.get("/bookings/mine"),
      api.get("/table-reservations/mine"),
      api.get("/services/requests/mine"),
      api.get("/ambulance/bookings/mine"),
      api.get("/payments/mine"),
      api.get("/disputes/mine"),
    ]);

    const detail = [o, a, b, tasks, ambulance, p, d];
    setPartialLoad(detail.some((s) => s.status === "rejected"));
    setReservationsError(r.status === "rejected");

    if (o.status === "fulfilled") setOrdersById(byId(o.value.data));
    if (a.status === "fulfilled") setAppointmentsById(byId(a.value.data));
    if (b.status === "fulfilled") setBookingsById(byId(b.value.data));
    if (tasks.status === "fulfilled") setTasksById(byId(tasks.value.data));
    if (ambulance.status === "fulfilled") setAmbulanceById(byId(ambulance.value.data));
    if (r.status === "fulfilled") setReservations(rows(r.value.data));
    if (p.status === "fulfilled") setPayments(rows(p.value.data));
    if (d.status === "fulfilled") setDisputes(rows(d.value.data));
    setLoaded(true);
  };

  const loadInitial = () => {
    setLoadError(null);
    reload().catch((e) => setLoadError(apiErrorMessage(e, t("errors.generic"))));
  };

  const disputeFor = (sourceType, sourceId) =>
    disputes.find((d) => d.source_type === sourceType && d.source_id === sourceId);

  const submitDispute = async (item) => {
    setReportBusy(true);
    try {
      await api.post("/disputes", { source_type: item.source_type, source_id: item.source_id, reason: reportReason });
      toast.success(t("disputes.submitted"));
      setReportingId("");
      setReportReason("");
      await reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setReportBusy(false);
    }
  };

  useEffect(() => {
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  // A UPI payment has no gateway callback — the only way to see the owner's "received" land is to
  // keep asking, same cadence as the mobile app's usePayment polling. Only run while something is
  // actually still open, not forever.
  const hasOpenPayment = payments.some((p) => OPEN_PAYMENT_STATUSES.includes(p.status));
  useEffect(() => {
    if (!hasOpenPayment) return;
    const interval = setInterval(() => {
      api.get("/payments/mine").then((r) => setPayments(rows(r.data)));
    }, 4000);
    return () => clearInterval(interval);
  }, [hasOpenPayment]);

  const paymentFor = (sourceType, sourceId) =>
    payments.find((p) => p.source_type === sourceType && p.source_id === sourceId);

  const claimPayment = async (payment, utr) => {
    setPaymentBusyId(payment.id);
    setPaymentErrors((e) => ({ ...e, [payment.id]: "" }));
    try {
      await api.post(`/payments/${payment.id}/claim`, { utr });
      await reload();
    } catch (e) {
      setPaymentErrors((err) => ({ ...err, [payment.id]: apiErrorMessage(e, t("errors.generic")) }));
    } finally {
      setPaymentBusyId("");
    }
  };

  const cancelPayment = async (payment) => {
    setPaymentBusyId(payment.id);
    try {
      await api.post(`/payments/${payment.id}/cancel`);
      await reload();
    } catch (e) {
      setPaymentErrors((err) => ({ ...err, [payment.id]: apiErrorMessage(e, t("errors.generic")) }));
    } finally {
      setPaymentBusyId("");
    }
  };

  // R4-10: cancelling is not undoable - ask first. The buttons call `askCancel`; only the dialog's
  // confirm reaches `cancel`.
  const [pendingCancel, setPendingCancel] = useState(null);
  const askCancel = (kind, id) => setPendingCancel({ kind, id });

  const cancel = async (kind, id) => {
    try {
      await api.post(`/${kind}/${id}/transition`, { status: "cancelled" });
      toast.success(t("ownerOps.statusChanged"));
      await reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const cancelTask = async (id) => {
    try {
      await api.post(`/services/requests/${id}/cancel`);
      toast.success(t("ownerOps.statusChanged"));
      await reload();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  // L4: rows used to be dead cards. The detail pages exist (/order/:id …), so each row links to its
  // own. A real <Link> beside the content rather than wrapping the whole card: the card holds
  // buttons, inputs and a payment panel, and an anchor may not contain interactive content.
  const detailLink = (to, id) =>
    isUuid(id) ? (
      <Link to={to} className="text-xs font-medium text-[hsl(var(--primary))] underline" data-testid={`activity-link-${id}`}>
        {t("activity.viewDetails")}
      </Link>
    ) : null;

  // RF-8: the whole row opens its detail page, not just the small link. Clicks that land on an
  // inner control (Cancel, the payment panel's inputs, the "View details" link itself) are left to
  // that control — only a click on the card's own surface navigates.
  const rowNav = (to, id) =>
    isUuid(id)
      ? {
          onClick: (e) => {
            if (e.target.closest("button, a, input, textarea, select, label, [role='button']")) return;
            navigate(to);
          },
        }
      : {};

  const renderOrder = (o) => {
    const payment = paymentFor("order", o.id);
    return (
      <article key={o.id} className="cursor-pointer rounded-2xl border border-border p-5" data-testid={`order-${o.id}`} {...rowNav(`/order/${o.id}`, o.id)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-heading text-lg font-semibold">{o.business_name}</p>
            <p className="tabular-nums text-xs text-muted-foreground">#{shortOrderRef(o.id)}</p>
            {detailLink(`/order/${o.id}`, o.id)}
          </div>
          <Badge className="bg-[hsl(var(--primary))]" data-testid={`order-status-${o.id}`}>
            {t("commerce.statusLabels")[o.status]}
          </Badge>
        </div>
        <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
          {o.items_snapshot.map((i, idx) => (
            <li key={idx}>
              {/* `unit_price_minor_units`, and it is PAISE. `_line_snapshot` has never sent a
                  `unit_price`, so this multiplied `undefined` and every line read ₹0. */}
              {i.quantity} × {i.name} —{" "}
              {rupees({ minor_units: (i.unit_price_minor_units ?? 0) * i.quantity })}
            </li>
          ))}
        </ul>
        {/* F-06: itemise the discounts so the total reconciles. `pricing_snapshot` is minor
            units throughout (subtotal / offer_discount / discount / total). */}
        {(() => {
          const ps = o.pricing_snapshot || {};
          const subtotal = ps.subtotal ?? o.total_minor_units ?? 0;
          const offer = ps.offer_discount || 0;
          const points = ps.discount || 0;
          const total = o.total_minor_units ?? ps.total ?? subtotal;
          return (
            <div className="mt-2 space-y-0.5 tabular-nums text-sm">
              <p className="flex justify-between text-muted-foreground" data-testid={`order-subtotal-${o.id}`}>
                <span>{t("commerce.subtotal")}</span>
                <span>{rupees({ minor_units: subtotal })}</span>
              </p>
              {offer > 0 && (
                <p className="flex justify-between text-muted-foreground" data-testid={`order-offer-discount-${o.id}`}>
                  <span>{t("commerce.offerDiscount")}</span>
                  <span>− {rupees({ minor_units: offer })}</span>
                </p>
              )}
              {points > 0 && (
                <p className="flex justify-between text-muted-foreground" data-testid={`order-points-discount-${o.id}`}>
                  <span>{t("commerce.pointsDiscount")}</span>
                  <span>− {rupees({ minor_units: points })}</span>
                </p>
              )}
              <p className="flex justify-between font-semibold" data-testid={`order-total-${o.id}`}>
                <span>{t("commerce.total")}</span>
                <span>{rupees({ minor_units: total })}</span>
              </p>
            </div>
          );
        })()}
        <StatusTimeline timeline={o.timeline} labelsKey="commerce.statusLabels" testId={`order-timeline-${o.id}`} />
        {payment && (
          <PaymentPanel
            payment={payment}
            requireUtr={payment.kind === "checkout"}
            onClaim={(utr) => claimPayment(payment, utr)}
            onCancel={() => cancelPayment(payment)}
            busy={paymentBusyId === payment.id}
            error={paymentErrors[payment.id] || ""}
            testId={`order-payment-${o.id}`}
          />
        )}
        {["placed", "confirmed"].includes(o.status) && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3 rounded-full"
            data-testid={`cancel-order-${o.id}`}
            onClick={() => askCancel("orders", o.id)}
          >
            {t("commerce.cancelOrder")}
          </Button>
        )}
      </article>
    );
  };

  const renderAppointment = (a) => {
    const payment = paymentFor("appointment", a.id);
    return (
      <article key={a.id} className="cursor-pointer rounded-2xl border border-border p-5" data-testid={`appointment-${a.id}`} {...rowNav(`/appointments/${a.id}`, a.id)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-heading text-lg font-semibold">{a.doctor_name}</p>
            <p className="text-sm text-muted-foreground">{a.business_name}</p>
            {/* `slot_start_at`. Reading `slot_start` was `undefined.replace(...)` — a TypeError
                that the ErrorBoundary turned into "Something went wrong" for the WHOLE page, so
                any citizen with an appointment could not see their activity at all. */}
            <p className="tabular-nums text-xs">{dateTime(a.slot_start_at, lang)}</p>
            {detailLink(`/appointments/${a.id}`, a.id)}
            {a.fee ? (
              <p className="tabular-nums text-sm">
                {t("appointments.fee")}: {rupees({ minor_units: a.fee?.minor_units })}
              </p>
            ) : null}
          </div>
          <Badge className="bg-[hsl(var(--primary))]" data-testid={`appointment-status-${a.id}`}>
            {t("appointments.statusLabels")[a.status]}
          </Badge>
        </div>
        <StatusTimeline timeline={a.timeline} labelsKey="appointments.statusLabels" testId={`appointment-timeline-${a.id}`} />
        {payment && (
          <PaymentPanel
            payment={payment}
            requireUtr={payment.kind === "checkout"}
            onClaim={(utr) => claimPayment(payment, utr)}
            onCancel={() => cancelPayment(payment)}
            busy={paymentBusyId === payment.id}
            error={paymentErrors[payment.id] || ""}
            testId={`appointment-payment-${a.id}`}
          />
        )}
        {["requested", "confirmed", "rescheduled"].includes(a.status) && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3 rounded-full"
            data-testid={`cancel-appointment-${a.id}`}
            onClick={() => askCancel("appointments", a.id)}
          >
            {t("common.cancel")}
          </Button>
        )}
      </article>
    );
  };

  const renderBooking = (b) => {
    const payment = paymentFor("booking", b.id);
    return (
      <article key={b.id} className="cursor-pointer rounded-2xl border border-border p-5" data-testid={`booking-${b.id}`} {...rowNav(`/bookings/${b.id}`, b.id)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-heading text-lg font-semibold">{b.unit_name}</p>
            <p className="text-sm text-muted-foreground">{b.business_name}</p>
            <p className="text-xs">
              {/* `start_on` / `end_on` — dates, not `_date`. Both were blank. */}
              {shortDate(b.start_on, lang)} → {shortDate(b.end_on, lang)} ·{" "}
              {b.nights === 1 ? t("bookings.night") : t("bookings.nights", { count: b.nights })}
            </p>
            <p className="text-sm">
              {/* A booking has NO `pricing_snapshot` — `_booking_out` sends `total` as a money
                  object. This read threw on `undefined.total`, taking the whole page down for
                  anyone with a hotel booking. */}
              {t("bookings.totalPrice")}: {rupees({ minor_units: b.total?.minor_units })}
            </p>
            {detailLink(`/bookings/${b.id}`, b.id)}
          </div>
          <Badge className="bg-[hsl(var(--primary))]" data-testid={`booking-status-${b.id}`}>
            {(b.unit_type === "room" || b.unit_type == null ? {} : t("bookings.hallStatusLabels"))[b.status] ||
              t("bookings.statusLabels")[b.status]}
          </Badge>
        </div>
        <StatusTimeline timeline={b.timeline} labelsKey="bookings.statusLabels" testId={`booking-timeline-${b.id}`} labelOverrides={b.unit_type === "room" || b.unit_type == null ? undefined : t("bookings.hallStatusLabels")} />
        {payment && (
          <PaymentPanel
            payment={payment}
            requireUtr={payment.kind === "checkout"}
            onClaim={(utr) => claimPayment(payment, utr)}
            onCancel={() => cancelPayment(payment)}
            busy={paymentBusyId === payment.id}
            error={paymentErrors[payment.id] || ""}
            testId={`booking-payment-${b.id}`}
          />
        )}
        {["reserved", "confirmed"].includes(b.status) && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3 rounded-full"
            data-testid={`cancel-booking-${b.id}`}
            onClick={() => askCancel("bookings", b.id)}
          >
            {t("common.cancel")}
          </Button>
        )}
      </article>
    );
  };

  const renderTask = (task) => {
    // L9 / RF-8: the agreed price. `_request_out` has never sent `assigned_quote_id`, so the old
    // lookup of the chosen quote always missed; `accepted_price` is the request's own copy of it.
    const agreed = task.accepted_price;
    return (
    <article key={task.id} className="cursor-pointer rounded-2xl border border-border p-5" data-testid={`task-${task.id}`} {...rowNav(`/tasks/${task.id}`, task.id)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading text-lg font-semibold">{tradeLabel(lang, task)}</p>
          {task.area && <p className="text-sm text-muted-foreground">{task.area}</p>}
          <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>
          {detailLink(`/tasks/${task.id}`, task.id)}
        </div>
        <Badge className="bg-[hsl(var(--primary))]" data-testid={`task-status-${task.id}`}>
          {t("tasks.statusLabels")[task.status]}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("tasks.quotesSoFar", { count: task.quotes?.length ?? task.quote_count ?? 0 })}
      </p>
      {agreed?.minor_units != null && (
        <p className="mt-1 tabular-nums text-sm" data-testid={`task-amount-${task.id}`}>
          {t("tasks.agreedAmount")}: {rupees({ minor_units: agreed.minor_units })}
        </p>
      )}
      <StatusTimeline timeline={task.timeline} labelsKey="tasks.statusLabels" testId={`task-timeline-${task.id}`} />
      {TASK_OPEN_STATUSES.includes(task.status) && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3 rounded-full"
          data-testid={`cancel-task-${task.id}`}
          onClick={() => cancelTask(task.id)}
        >
          {t("common.cancel")}
        </Button>
      )}
    </article>
    );
  };

  const renderAmbulance = (amb) => (
    <Link key={amb.id} to={`/ambulance/${amb.id}`} data-testid={`ambulance-${amb.id}`}>
      <article className="rounded-2xl border border-border p-5 transition-colors hover:bg-muted/40">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("ambulance.title")}
            </p>
            <p className="font-heading text-lg font-semibold">{amb.destination || amb.pickup_address}</p>
          </div>
          <Badge className="bg-[hsl(var(--primary))]" data-testid={`ambulance-status-${amb.id}`}>
            {t("ambulance.statusLabels")[amb.status]}
          </Badge>
        </div>
      </article>
    </Link>
  );

  // Fallback for a spine row whose per-type detail record didn't come back (e.g. a transient fetch
  // hiccup) — keeps the row visible with the generic spine fields rather than silently dropping it.
  const renderFallback = (item) => (
    <article key={item.id} className="rounded-2xl border border-border p-5" data-testid={`activity-fallback-${item.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading text-lg font-semibold">{item.title || item.provider_name}</p>
        <Badge className="bg-[hsl(var(--primary))]">{item.status}</Badge>
      </div>
    </article>
  );

  const renderRow = (item) => {
    switch (item.source_type) {
      case "order":
        return ordersById[item.source_id] ? renderOrder(ordersById[item.source_id]) : renderFallback(item);
      case "appointment":
        return appointmentsById[item.source_id] ? renderAppointment(appointmentsById[item.source_id]) : renderFallback(item);
      case "booking":
        return bookingsById[item.source_id] ? renderBooking(bookingsById[item.source_id]) : renderFallback(item);
      case "service_request":
        return tasksById[item.source_id] ? renderTask(tasksById[item.source_id]) : renderFallback(item);
      case "ambulance":
        return ambulanceById[item.source_id] ? renderAmbulance(ambulanceById[item.source_id]) : renderFallback(item);
      default:
        return renderFallback(item);
    }
  };

  if (loadError) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="activity-page">
        <h1 className="font-heading text-4xl font-bold tracking-tight">{t("activity.title")}</h1>
        <div
          className="mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          data-testid="activity-error"
        >
          {loadError}
          <Button size="sm" variant="outline" className="rounded-full" data-testid="activity-retry" onClick={loadInitial}>
            {t("common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="activity-page">
        <h1 className="font-heading text-4xl font-bold tracking-tight">{t("activity.title")}</h1>
        <div className="mt-8 space-y-4" data-testid="activity-loading">
          <Skeleton className="h-10 w-56 rounded-full" />
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-28 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  const openItems = items.filter((i) => i.is_open);
  const pastItems = items.filter((i) => !i.is_open);
  // The "Going on" count is every open kind. Table reservations are listed in their own section
  // (and may not ride the feed), so an active one is counted here unless the feed already has it.
  const feedIds = new Set(items.map((i) => i.source_id));
  const openReservations = reservations.filter(
    (r) => ["requested", "confirmed"].includes(r.status) && !feedIds.has(r.id),
  );
  const goingOnCount = openItems.length + openReservations.length;

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="activity-page">
      <ConfirmDialog
        open={Boolean(pendingCancel)}
        onOpenChange={(open) => !open && setPendingCancel(null)}
        title={t("commerce.cancelConfirmTitle")}
        body={t("commerce.cancelConfirmBody")}
        confirmLabel={t("commerce.cancelConfirmYes")}
        cancelLabel={t("commerce.cancelKeep")}
        destructive
        onConfirm={async () => {
          const { kind, id } = pendingCancel;
          setPendingCancel(null);
          await cancel(kind, id);
        }}
        testIdPrefix="cancel-confirm"
      />
      <h1 className="font-heading text-4xl font-bold tracking-tight">{t("activity.title")}</h1>

      {partialLoad && (
        <div
          className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
          data-testid="activity-partial"
        >
          {t("activity.partialLoad")}
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            data-testid="activity-partial-retry"
            onClick={loadInitial}
          >
            {t("common.retry")}
          </Button>
        </div>
      )}

      <MyMemberships />
      <MyAttendance />
      <MyCommunity />

      <Tabs defaultValue="open" className="mt-8">
        <TabsList data-testid="activity-group-tabs">
          <TabsTrigger value="open" data-testid="activity-group-open">
            {t("activity.group.open")} ({goingOnCount})
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="activity-group-past">
            {t("activity.group.past")} ({pastItems.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="open" className="mt-6 space-y-4" data-testid="activity-open-list">
          {openItems.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="activity-empty-open">
              <p className="text-muted-foreground">{t("activity.emptyOpen")}</p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Button asChild className="rounded-full">
                  <Link to="/directory" data-testid="activity-browse-btn">{t("activity.browseShops")}</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full">
                  <Link to="/services" data-testid="activity-find-worker-btn">{t("activity.findWorker")}</Link>
                </Button>
              </div>
            </div>
          )}
          {openItems.map((item) => (
            <React.Fragment key={item.id}>{renderRow(item)}</React.Fragment>
          ))}
        </TabsContent>

        <TabsContent value="past" className="mt-6 space-y-4" data-testid="activity-past-list">
          {pastItems.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="activity-empty-past">
              <p className="text-muted-foreground">{t("activity.emptyPast")}</p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Button asChild className="rounded-full">
                  <Link to="/directory" data-testid="activity-browse-btn">{t("activity.browseShops")}</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full">
                  <Link to="/services" data-testid="activity-find-worker-btn">{t("activity.findWorker")}</Link>
                </Button>
              </div>
            </div>
          )}
          {pastItems.map((item) => {
            const dispute = disputeFor(item.source_type, item.source_id);
            return (
              <React.Fragment key={item.id}>
                {renderRow(item)}
                <div className="-mt-3 flex justify-end" data-testid={`dispute-action-${item.id}`}>
                  {dispute ? (
                    <Badge variant="outline">{t(`disputes.statusLabels.${dispute.status}`)}</Badge>
                  ) : reportingId === item.id ? (
                    <div className="w-full space-y-2 rounded-2xl border border-border p-4">
                      <Textarea
                        data-testid={`dispute-reason-${item.id}`}
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                        placeholder={t("disputes.reasonPlaceholder")}
                        rows={2}
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setReportingId("")}>
                          {t("common.cancel")}
                        </Button>
                        <Button
                          data-testid={`dispute-submit-${item.id}`}
                          disabled={reportBusy || !reportReason.trim()}
                          onClick={() => submitDispute(item)}
                        >
                          {t("disputes.submit")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="text-xs text-muted-foreground underline"
                      data-testid={`dispute-report-btn-${item.id}`}
                      onClick={() => {
                        setReportingId(item.id);
                        setReportReason("");
                      }}
                    >
                      {t("disputes.reportIssue")}
                    </button>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </TabsContent>
      </Tabs>

      <div className="mt-10">
        <h2 className="font-heading text-xl font-semibold">{t("activity.reservationsTitle")}</h2>
        <div className="mt-4 space-y-4" data-testid="reservations-list">
          {/* A failed fetch must not borrow the empty state's words. "No reservations yet" to
           * someone who has a table booked tonight is worse than an error. */}
          {reservationsError ? (
            <div
              className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              data-testid="reservations-error"
            >
              {t("activity.reservationsFailed")}
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                data-testid="reservations-retry"
                onClick={loadInitial}
              >
                {t("common.retry")}
              </Button>
            </div>
          ) : (
            reservations.length === 0 && <p className="text-muted-foreground">{t("tables.noReservations")}</p>
          )}
          {reservations.map((r) => (
            <article key={r.id} className="rounded-2xl border border-border p-5" data-testid={`reservation-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-heading text-lg font-semibold">{r.business_name}</p>
                  {/* `slot_start_at`, as on the appointment above. Same crash. */}
                  <p className="tabular-nums text-xs">{dateTime(r.slot_start_at, lang)}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("tables.partySize")}: {r.party_size}
                  </p>
                </div>
                <Badge className="bg-[hsl(var(--primary))]" data-testid={`reservation-status-${r.id}`}>
                  {t("tables.statusLabels")[r.status]}
                </Badge>
              </div>
              <StatusTimeline timeline={r.timeline} labelsKey="tables.statusLabels" testId={`reservation-timeline-${r.id}`} />
              {["requested", "confirmed"].includes(r.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 rounded-full"
                  data-testid={`cancel-reservation-${r.id}`}
                  onClick={() => askCancel("table-reservations", r.id)}
                >
                  {t("common.cancel")}
                </Button>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
